import { RefObject } from 'preact';
import { useState } from 'preact/hooks';
import { ITEM_SELECTOR, dragToScroll } from '../utils/PRIVATE_collectionUtils';
import { useReorderable } from '../hooks/PRIVATE_useReorderable';
import { Props } from '../UNSAFE_CardFlexView';
import { GutterSize } from './BaseCardViewUtils';

type useCardViewReorderProps<K, D> = Pick<Props<K, D>, 'currentKey' | 'onReorder'> & {
  gutterSize: GutterSize;
  columns: number;
  rootRef: RefObject<HTMLDivElement>;
  scrollCurrItemIntoView: RefObject<boolean>;
};

export const useCardViewReorder = <K extends string | number, D>({
  currentKey,
  onReorder,
  gutterSize,
  columns,
  rootRef,
  scrollCurrItemIntoView
}: useCardViewReorderProps<K, D>) => {
  const isReorderEnabled = onReorder != undefined;

  const [dragKey, setDragKey] = useState<K | undefined>(undefined);
  const { reorderProps, reorderContext, reorderInstructionsId, reorderInstructions } =
    useReorderable({
      onReorder,
      rootRef,
      itemSelector: ITEM_SELECTOR,
      isDisabled: !isReorderEnabled,
      columns
    });

  if (!isReorderEnabled) {
    return {
      reorderProps,
      reorderContext,
      reorderInstructionsId,
      reorderInstructions
    } as const;
  }

  const handleDragOver = (event: DragEvent) => {
    dragToScroll(event, dragKey !== undefined, rootRef.current);
    // during mouse reordering, the drag item might not be the same as current item,
    // so we should disallow current item scroll into view
    scrollCurrItemIntoView.current = false;
  };

  const reorderEventProps = { ...reorderProps, onDragOver: handleDragOver };

  const reorderItemProps = {
    currentKey,
    dragKey,
    setDragKey,
    onReorder,
    rootRef,
    gutterSize,
    columns
  };

  return {
    reorderProps: reorderEventProps,
    reorderItemProps,
    reorderContext,
    reorderInstructionsId,
    reorderInstructions
  } as const;
};
